import {
  BeneficiaryInvestmentProfile,
  DropdownInvestmentOption,
  InvestmentOptionAPIType,
} from "@/types/investmentProfile";
import Dropdown from "@components/Common/Input/Dropdown";

interface BeneficiaryCardProps {
  beneficiary: BeneficiaryInvestmentProfile;
  isLastItem: boolean;
  getAcademicYearOptions: (beneficiaryId: number) => DropdownInvestmentOption[];
  getFilteredInvestmentOptions: (
    academicYear: string
  ) => DropdownInvestmentOption[];
  getSelectedPortfolioDetails: (
    investmentOptionId: string
  ) => InvestmentOptionAPIType | null;
  onOptionChange: (index: number, option: string) => void;
  onAcademicYearChange: (index: number, academicYear: string) => void;
  index: number;
  isUserEnrolled?: boolean;
}

const BeneficiaryCard: React.FC<BeneficiaryCardProps> = ({
  beneficiary,
  isLastItem,
  getAcademicYearOptions,
  getFilteredInvestmentOptions,
  getSelectedPortfolioDetails,
  onOptionChange,
  onAcademicYearChange,
  index,
  isUserEnrolled,
}) => {
  const academicYearOptions = getAcademicYearOptions(beneficiary.id);
  const filteredInvestmentOptions = getFilteredInvestmentOptions(
    beneficiary.selectedAcademicTargetYear
  );
  const selectedPortfolio = getSelectedPortfolioDetails(
    beneficiary.selectedInvestmentOptionID
  );

  const selectedInvestmentValue =
    filteredInvestmentOptions.find(
      (option) => option.value === beneficiary.selectedInvestmentOptionID
    )?.value || "";

  const creditCost = selectedPortfolio?.currentNavValue || 0;
  const totalBeneficiaryContribution =
    (beneficiary.employeeMinorContributionInDollar || 0) +
    (beneficiary.employerMinorContributionInDollar || 0);
  const creditsPerCheck =
    totalBeneficiaryContribution > 0
      ? (totalBeneficiaryContribution / creditCost).toFixed(2)
      : "0.00";

  return (
    <div
      className={`${
        isUserEnrolled
          ? "pb-[32px]"
          : `px-[16px] py-[32px] md:p-[32px] ${!isLastItem && "border-b-[1px]"}`
      }`}
    >
      <div className="flex justify-between items-center h-full">
        <div className="flex flex-col space-y-[8px] w-full">
          <h3 className="text-[18px] leading-[127%]">{beneficiary.name}</h3>
          {beneficiary.investmentPlanType === "recommendation" && (
            <p className="text-[#8B8B8B] text-[16px] leading-[127%]">
              We recommended this option based on{" "}
              {beneficiary.name.split(" ")[0]}
              &apos;s age & goal.
            </p>
          )}

          <div className="pt-[16px]">
            <Dropdown
              name="academicYear"
              label="Academic Target Year"
              value={beneficiary.selectedAcademicTargetYear}
              options={academicYearOptions}
              onChange={(e) => onAcademicYearChange(index, e.target.value)}
            />
          </div>
          <div className="pt-[16px]">
            <Dropdown
              name="investmentOptions"
              label="Investment Options"
              value={selectedInvestmentValue}
              options={filteredInvestmentOptions}
              onChange={(e) => onOptionChange(index, e.target.value)}
            />
          </div>

          <div className="pt-8">
            <div className="bg-[#F8F8F8] rounded-[8px] p-4 text-[16px]">
              {selectedPortfolio && (
                <div className="flex space-x-2 mb-4">
                  <p>
                    Credit Cost:{" "}
                    <span className="font-semibold">
                      ${creditCost.toFixed(2)}
                    </span>
                  </p>
                  <p>|</p>
                  <p>
                    Credits per Check:{" "}
                    <span className="font-semibold">{creditsPerCheck}</span>{" "}
                  </p>
                </div>
              )}
              <p className="text-black ">
                {selectedPortfolio?.additionalDetails ||
                  "Please select an investment option to see details."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeneficiaryCard;
